import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Select, Skeleton } from "antd";
import { ExportOutlined, ReloadOutlined } from "@ant-design/icons";
import { AuthContext } from "../../context/auth-context/AuthContext";
import apiService from "../../services/apiServices";

const claveSemana = (etiqueta) => {
  const m = /Semana (\d+).*?(\d{4})\s*$/.exec(etiqueta || "");
  return m ? Number(m[2]) * 100 + Number(m[1]) : 0;
};

const rotuloSemana = (etiqueta) => {
  const m = /\((\d{2}\/\d{2})/.exec(etiqueta || "");
  return m ? m[1] : etiqueta;
};

const formatoTiempo = (seg) => {
  if (!seg) return "0:00";
  const s = Math.round(seg);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const numero = (n) => (n || 0).toLocaleString("es-AR");

export const Analiticas = ({ onAbrirTableroCompleto }) => {
  const { authState } = useContext(AuthContext);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [estado, setEstado] = useState(null);
  const [filtro, setFiltro] = useState("todas");

  const cargar = useCallback(async () => {
    setError(null);
    setDatos(null);
    try {
      setDatos(await apiService.getDashboardMetricas(authState.userId));
    } catch (e) {
      setDatos({});
      setError("No pudimos cargar las métricas. Intentá de nuevo en unos minutos.");
    }
    apiService
      .getEstadoAsistente()
      .then(setEstado)
      .catch(() => setEstado(null));
  }, [authState.userId]);

  useEffect(() => {
    if (authState.userId) cargar();
  }, [authState.userId, cargar]);

  const experiencias = datos?.lista_experiencias || [];
  const nombreDe = useMemo(() => {
    const m = new Map(experiencias.map((e) => [e.id, e.nombre]));
    return (id) => m.get(id) || "Experiencia eliminada";
  }, [experiencias]);

  const vista = useMemo(() => {
    if (!datos) return null;
    const incluye = (id) => filtro === "todas" || id === filtro;
    const generales = (datos.metrica_generales || []).filter((m) => incluye(m.id_experiencia));

    const visitas = generales.reduce((a, m) => a + (m.visitas || 0), 0);
    const tiempos = generales.filter((m) => m.promedio_tiempo_visita_segundos > 0);
    const tiempoProm = tiempos.length
      ? tiempos.reduce((a, m) => a + m.promedio_tiempo_visita_segundos * (m.visitas || 1), 0) /
        tiempos.reduce((a, m) => a + (m.visitas || 1), 0)
      : 0;

    const porSemana = new Map();
    (datos.metrica_semanales || [])
      .filter((s) => incluye(s.id_exp))
      .forEach((s) => {
        const prev = porSemana.get(s.semana) || 0;
        porSemana.set(s.semana, prev + (s.visitas || 0));
      });
    const semanas = [...porSemana.entries()]
      .sort((a, b) => claveSemana(a[0]) - claveSemana(b[0]))
      .slice(-16)
      .map(([semana, v]) => ({ semana, visitas: v }));

    const ranking = generales
      .map((m) => ({ id: m.id_experiencia, nombre: nombreDe(m.id_experiencia), visitas: m.visitas || 0 }))
      .sort((a, b) => b.visitas - a.visitas)
      .slice(0, 6);

    const ciudades = new Map();
    (datos.extendedGeoData || [])
      .filter((g) => incluye(g.id_exp))
      .forEach((g) => {
        const k = `${g.city}, ${g.country}`;
        ciudades.set(k, (ciudades.get(k) || 0) + (g.count || 0));
      });
    const topCiudades = [...ciudades.entries()].sort((a, b) => b[1] - a[1]);

    return {
      kpis: [
        ["Visitas", numero(visitas)],
        ["Tiempo promedio", formatoTiempo(tiempoProm)],
        ["Interacciones", numero(generales.reduce((a, m) => a + (m.clics_en_relaciones || 0), 0))],
        ["Visitas al carrito", numero(generales.reduce((a, m) => a + (m.agregados_al_carrito || 0), 0))],
        ["Compartidas", numero(generales.reduce((a, m) => a + (m.cantidad_compartido || 0), 0))],
      ],
      semanas,
      ranking,
      ciudades: topCiudades.slice(0, 5),
      otrasCiudades: topCiudades.slice(5).reduce((a, [, n]) => a + n, 0),
      hayDatos: generales.length > 0,
    };
  }, [datos, filtro, nombreDe]);

  const asistente = useMemo(() => {
    if (!estado?.experiencias) return null;
    const ids = new Set(experiencias.map((e) => e.id));
    const propias = Object.entries(estado.experiencias).filter(
      ([id]) => ids.has(id) && (filtro === "todas" || id === filtro)
    );
    return {
      charlas: propias.reduce((a, [, e]) => a + (e.sesionesDelMes || 0), 0),
      minutos: propias.reduce((a, [, e]) => a + (e.minutosUsados || 0), 0),
    };
  }, [estado, experiencias, filtro]);

  const maxSemana = Math.max(1, ...(vista?.semanas || []).map((s) => s.visitas));
  const maxRanking = Math.max(1, ...(vista?.ranking || []).map((r) => r.visitas));

  return (
    <div className="inm-pagina">
      <header className="inm-pagina__cabecera">
        <h1 className="inm-pagina__titulo">Analíticas</h1>
        <Select
          aria-label="Experiencia"
          value={filtro}
          onChange={setFiltro}
          style={{ minWidth: 240, maxWidth: "100%" }}
          options={[
            { value: "todas", label: "Todas las experiencias" },
            ...experiencias.map((e) => ({ value: e.id, label: e.nombre })),
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={cargar} aria-label="Actualizar" />
        {process.env.REACT_APP_BASE_URL_ANALYTICS && (
          <Button icon={<ExportOutlined />} onClick={onAbrirTableroCompleto}>
            Tablero completo
          </Button>
        )}
      </header>

      {error && <Alert type="error" showIcon message={error} />}

      {!vista ? (
        <div className="inm-caja">
          <Skeleton active />
        </div>
      ) : !vista.hayDatos && !error ? (
        <div className="inm-caja" style={{ alignItems: "center", padding: 40 }}>
          <Empty description="Todavía no hay visitas registradas. Compartí el link de una experiencia para empezar a medir." />
        </div>
      ) : (
        <>
          <div className="inm-kpis">
            {vista.kpis.map(([etiqueta, valor]) => (
              <div key={etiqueta} className="inm-kpi">
                <span>{etiqueta}</span>
                <b>{valor}</b>
              </div>
            ))}
          </div>

          <div className="inm-fila">
            <section className="inm-caja inm-principal" aria-labelledby="an-semanas">
              <h2 id="an-semanas" className="inm-h2">
                Visitas por semana
              </h2>
              {vista.semanas.length ? (
                <>
                  <div className="inm-barras" role="img" aria-label="Visitas por semana">
                    {vista.semanas.map((s) => (
                      <i
                        key={s.semana}
                        title={`${s.semana}: ${s.visitas} visitas`}
                        style={{ height: `${Math.max(3, (s.visitas / maxSemana) * 100)}%` }}
                      />
                    ))}
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }} className="inm-tenue">
                    <span>{rotuloSemana(vista.semanas[0].semana)}</span>
                    <span>{rotuloSemana(vista.semanas[vista.semanas.length - 1].semana)}</span>
                  </div>
                </>
              ) : (
                <p className="inm-tenue">Sin visitas en el período.</p>
              )}
            </section>

            <section className="inm-caja inm-caja--asistente inm-lateral" aria-labelledby="an-asistente">
              <h2 id="an-asistente" className="inm-h2">
                Asistente
              </h2>
              {asistente ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span className="inm-tenue">Charlas del mes</span>
                    <b style={{ font: "600 26px var(--inm-titulos)" }}>{numero(asistente.charlas)}</b>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <span className="inm-tenue">Minutos</span>
                    <b style={{ font: "600 26px var(--inm-titulos)" }}>{numero(asistente.minutos)}</b>
                  </div>
                </div>
              ) : (
                <p className="inm-tenue" style={{ margin: 0 }}>Sin datos del asistente.</p>
              )}
              <p className="inm-tenue" style={{ margin: 0 }}>
                Cuenta desde el último reinicio del servidor. Para ver lo que más preguntan hace falta guardar las
                transcripciones de las charlas.
              </p>
            </section>
          </div>

          <div className="inm-fila">
            <section className="inm-caja inm-principal" aria-labelledby="an-ranking">
              <h2 id="an-ranking" className="inm-h2">
                Experiencias más visitadas
              </h2>
              {vista.ranking.map((r) => (
                <div key={r.id} className="inm-renglon">
                  <span className="inm-renglon__nombre" title={r.nombre}>
                    {r.nombre}
                  </span>
                  <span className="inm-pista">
                    <i style={{ width: `${(r.visitas / maxRanking) * 100}%` }} />
                  </span>
                  <span className="inm-renglon__valor">{numero(r.visitas)}</span>
                </div>
              ))}
            </section>
            <section className="inm-caja inm-lateral" aria-labelledby="an-ciudades">
              <h2 id="an-ciudades" className="inm-h2">
                De dónde visitan
              </h2>
              {vista.ciudades.length ? (
                <>
                  {vista.ciudades.map(([ciudad, n]) => (
                    <div key={ciudad} className="inm-renglon">
                      <span style={{ flexGrow: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {ciudad}
                      </span>
                      <span className="inm-renglon__valor">{numero(n)}</span>
                    </div>
                  ))}
                  {vista.otrasCiudades > 0 && (
                    <div className="inm-renglon">
                      <span style={{ flexGrow: 1 }}>Otras</span>
                      <span className="inm-renglon__valor">{numero(vista.otrasCiudades)}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="inm-tenue" style={{ margin: 0 }}>Sin datos de ubicación.</p>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};
